import React, { useEffect, useRef } from 'react';
import { classNames } from 'primereact/utils';
import { getFileTypeFromMimeType, replaceLawftDate, returnFileType } from 'utils/utils';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { presignedURLGet } from 'modules/file_manager/services';
import { useNavbarContext } from 'context/NavbarContext';
import constants from 'constants/index';
import { generateFullName } from 'utils/generateFullNameUtils';
import { SentimentDot } from 'components/SentimentDot/SentimentDot';

const messageStatus = (status) => {
  switch (status) {
    case 'delivered':
      return 'Delivered';
    case 'undelivered':
      return 'Not Delivered';
    case 'failed':
      return 'Failed';
    case 'sent':
      return 'Sent';
    case 'initiated':
      return 'Sending';
    default:
      return 'Not Delivered';
  }
};

const getUserName = (data = false) => {
  if (data && data.created_by && data.created_by.first_name) {
    return `${data.created_by.first_name} ${data.created_by.last_name}`;
  } else {
    return 'User';
  }
};

const getExtension = (file) => file?.media_url?.split('.')?.reverse()?.[0];

function Message({ selectedThread, message, index, setRowHeight }) {
  const { firmPhoneNumber, userDetails } = useUserDetailsContext();
  const { downloadFile: _download } = useNavbarContext();

  const downloadFile = async (file) => {
    try {
      if (file && file.mms_path) {
        const file_path = file.mms_path;
        const data = await presignedURLGet(file_path);
        const presignedUrl = data?.data?.file_url;
        if (presignedUrl) {
          _download(presignedUrl, file.display_filename || 'download');
        }
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const downloadUrl = async (url, filePath) => {
    try {
      // Extract file path from URL if filePath is not provided
      let path = filePath;
      if (!path && url) {
        // Try to extract path from S3 URL
        // URL format: https://bucket.s3.region.amazonaws.com/public/path/to/file
        // or: https://bucket.s3-region.amazonaws.com/public/path/to/file
        const urlMatch = url.match(/\/public\/(.+)$/);
        if (urlMatch) {
          path = `public/${urlMatch[1]}`;
        } else {
          // If no /public/ found, try to extract after the domain
          const domainMatch = url.match(/amazonaws\.com\/(.+)$/);
          if (domainMatch) {
            path = domainMatch[1];
          }
        }
      }

      if (path) {
        const data = await presignedURLGet(path);
        const presignedUrl = data?.data?.file_url;
        if (presignedUrl) {
          const filename = url.split('/').pop() || 'download';
          _download(presignedUrl, filename);
        }
      } else {
        // Fallback: try direct download if path extraction fails
        const filename = url.split('/').pop() || 'download';
        _download(url, filename);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const myself = firmPhoneNumber === message.from;

  const rowRef = useRef({});

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
  }, [rowRef]); // eslint-disable-line

  return (
    <div
      key={index}
      ref={rowRef}
      className={myself ? 'd-flex flex-wrap py-2 my-chat flex-column' : 'd-flex flex-wrap py-2 reply-chat flex-column'}
    >
      <div className="d-flex justify-content-end user-profile">
        <div className="user-dtls">
          <div className={myself ? 'F-size12 me-2 text-end mb-2' : 'F-size12 me-2 mb-2'}>
            {myself ? getUserName(message) : generateFullName(selectedThread?.client_name)}
          </div>
          <div className="F-size12"></div>
        </div>
      </div>

      <div className="d-flex justify-content-end reply-body">
        <div className="border shadow-small p-2 my-1 msg-box d-flex justify-content-between me-2">
          <div className="d-flex flex-column">
            <div className="d-flex text-break mb-2">{replaceLawftDate(message?.body, userDetails?.timezone) ?? null}</div>
            <div className="ms-auto text-message-files">
              {message?.media_urls?.length > 0
                ? message.media_urls.map((url, i) => {
                    const ext = url?.split('.').pop();
                    const fileType = returnFileType(ext);
                    const isImage = fileType === 'image' || getFileTypeFromMimeType(message?.media_content_types?.[i])?.includes('image');
                    const isVideo = fileType === 'video' || getFileTypeFromMimeType(message?.media_content_types?.[i])?.includes('video');
                    const isAudio = fileType === 'audio' || getFileTypeFromMimeType(message?.media_content_types?.[i])?.includes('audio');
                    const displayFilename = url.split('/').pop() || 'File';
                    // Get file path from mms_paths array if available, otherwise extract from URL
                    const filePath = message?.mms_paths?.[i] || null;

                    return (
                      <div key={i} className="position-relative mb-2 d-flex justify-content-end mb-3 mx-1">
                        <span className="icon-save download-file-icon shadow" onClick={() => downloadUrl(url, filePath)}></span>
                        {isImage ? (
                          <img
                            src={url}
                            className="chat-image"
                            alt=""
                            width="250"
                            height="160"
                            style={{ minHeight: 100, width: '250px', height: '160px', objectFit: 'contain' }}
                            onLoad={() => setRowHeight(index, rowRef.current.clientHeight)}
                          />
                        ) : isVideo ? (
                          <video
                            controls
                            className="videoInsert"
                            src={url}
                            autoPlay={false}
                            preload="none"
                            style={{ width: '250px', height: '160px', objectFit: 'cover' }}
                          >
                            <source src={url} type="video/mp4" />
                            <source src={url} type="video/ogg" />
                            <source src={url} type="video/mov" />
                            <source src={url} type="video/MOV" />
                          </video>
                        ) : isAudio ? (
                          <span className="message-file-container">
                            <i className="mb-1 message-file-icon fa fa-solid fa-file-audio"></i>
                            <span className="text-truncate">{` ${displayFilename}`}</span>
                          </span>
                        ) : (
                          <span className="message-file-container">
                            <i className="icon-casedocument-filled message-file-icon"></i>
                            <span className="text-truncate">{` ${displayFilename}`}</span>
                          </span>
                        )}
                      </div>
                    );
                  })
                : message?.media_url && (
                    <React.Fragment>
                      <span className="icon-save download-file-icon shadow" onClick={() => downloadFile(message)}></span>
                      {getFileTypeFromMimeType(message?.MediaContentType0) === 'image' ||
                      returnFileType(getExtension(message)) === 'image' ? (
                        <img
                          src={message.media_url}
                          className="chat-image"
                          alt=""
                          width="200"
                          height="auto"
                          style={{ minHeight: 100 }}
                          onLoad={() => setRowHeight(index, rowRef.current.clientHeight)}
                        />
                      ) : getFileTypeFromMimeType(message?.MediaContentType0) === 'video' ||
                        returnFileType(getExtension(message)) === 'video' ? (
                        <video controls className="videoInsert" src={message.media_url} autoPlay={false} preload="none">
                          <source src={message.media_url} type="video/mp4" />
                          <source src={message.media_url} type="video/ogg" />
                          <source src={message.media_url} type="video/mov" />
                          <source src={message.media_url} type="video/MOV" />
                        </video>
                      ) : getFileTypeFromMimeType(message?.MediaContentType0) === 'audio' ||
                        returnFileType(getExtension(message)) === 'audio' ? (
                        <span className="message-file-container">
                          <i className="mb-1 message-file-icon fa fa-solid fa-file-audio"></i>
                          <span className="text-truncate">{` ${message.display_filename || 'Audio'}`}</span>
                        </span>
                      ) : (
                        <span className="message-file-container">
                          <i className="icon-casedocument-filled message-file-icon"></i>
                          <span className="text-truncate">{` ${message.display_filename || 'File'}`}</span>
                        </span>
                      )}
                    </React.Fragment>
                  )}
            </div>
          </div>
          {Boolean(message?.last_message_status === 'delivered' || message?.last_message_status === 'sent') && (
            <div className="d-flex align-items-end F-size12" style={{ marginLeft: '1rem' }}>
              {message?.last_message_status === 'delivered' && <i className="fas fa-check-double" style={{ color: 'lightgreen' }} />}
              {message?.last_message_status === 'sent' && <i className="fas fa-check" style={{ color: 'lightgreen' }} />}
            </div>
          )}
        </div>
        {message?.sentiment_analysis?.sentiment && (
          <SentimentDot tooltipPosition="top" type={message?.sentiment_analysis?.sentiment} typeOnly />
        )}
      </div>
      <p className={classNames({ 'F-size12': true, 'text-end': myself, 'mb-0': true })}>
        {convertDateFromApiToUserTimezone(message?.created_at, userDetails?.timezone, constants.hour_minute_12_format)}{' '}
        {myself && messageStatus(message?.last_message_status)}
      </p>
    </div>
  );
}
export default React.memo(Message);
